import React, { useState } from 'react';
import {
  ShoppingCart,
  Plus,
  Minus,
  ArrowRight,
  Package
} from 'lucide-react';
import { useCart } from '../context/CartContext';

const ProductCategoriesGrid = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Get cart context
  const { addToCart, cartItems = [], updateCartItemQuantity } = useCart() || {};

  // Complete product catalog with exact data
  const productCatalog = {
    '🧰 Arduino & Development Boards': [
      { id: 'ard-uno', name: 'Arduino Uno + USB Cable', price: 750, description: 'Complete Arduino Uno R3 with USB cable for programming and power. Perfect for beginners.' },
      { id: 'ard-nano', name: 'Arduino Nano + Cable', price: 450, description: 'Compact Arduino Nano with cable. Small form factor perfect for breadboard projects.' },
      { id: 'atmega-328p', name: 'Atmega 328P Microcontroller Set', price: 400, description: 'Atmega 328P microcontroller set for custom Arduino projects and circuit design.' },
      { id: 'nano-adapter', name: 'Arduino Nano Terminal Adapter', price: 250, description: 'Terminal adapter for Arduino Nano making connections easier with screw terminals.' },
      { id: 'ard-mega', name: 'Arduino Mega Atmega2560 + Cable', price: 1000, description: 'Arduino Mega with Atmega2560 and cable. More I/O pins and memory for complex projects.' },
      { id: 'esp8266', name: 'ESP8266 NodeMCU', price: 800, description: 'ESP8266 NodeMCU with WiFi capability for IoT projects and wireless communication.' }
    ],
    '🔍 Sensors & Modules': [
      { id: 'fingerprint', name: 'Fingerprint sensor FPM10A', price: 950, description: 'High-quality FPM10A fingerprint sensor for biometric security and access control systems.' },
      { id: 'mq3', name: 'MQ-3 Gas Sensor', price: 250, description: 'MQ-3 alcohol and gas sensor for breath analyzer and gas detection projects.' },
      { id: 'dht11', name: 'DHT11 Temp & Humidity Sensor', price: 250, description: 'DHT11 digital temperature and humidity sensor for weather monitoring and environmental control.' },
      { id: 'acs712', name: 'ACS712 Current Sensor', price: 250, description: 'ACS712 current sensor for measuring AC/DC current in power monitoring applications.' },
      { id: 'hc-sr04', name: 'HC-SR04 Ultrasonic Sensor', price: 250, description: 'HC-SR04 ultrasonic distance sensor for ranging and obstacle detection applications.' },
      { id: 'pir-motion', name: 'PIR Motion Sensor (HC-SR501)', price: 250, description: 'PIR motion sensor HC-SR501 for motion detection in security and automation systems.' }
    ],
    '📺 Display & Interface Modules': [
      { id: 'lcd-16x2', name: '16x2 LCD with I2C', price: 350, description: '16x2 character LCD display with I2C interface for easy connection and text display.' },
      { id: 'lcd-20x4', name: '20x4 LCD Display', price: 450, description: '20x4 character LCD display for larger text display and menu systems.' },
      { id: 'keypad-4x4', name: '4x4 Membrane Keypad', price: 250, description: '4x4 membrane keypad for user input and control interface applications.' },
      { id: 'sd-module', name: 'Micro SD Card Module', price: 250, description: 'Micro SD card module for data logging and storage applications.' }
    ],
    '⚙️ Motors & Motor Drivers': [
      { id: 'nema17', name: 'Nema 17 Stepper Motor', price: 650, description: 'Nema 17 stepper motor for precise positioning in CNC and 3D printer applications.' },
      { id: 'l298n', name: 'L298N Motor Driver', price: 250, description: 'L298N dual motor driver for controlling DC motors and stepper motors.' },
      { id: 'servo-45g', name: '45g SpringRC Servo Motor', price: 650, description: '45g SpringRC servo motor for robotics and precise angular positioning.' },
      { id: 'servo-9g', name: '9g servo motors', price: 200, description: '9g micro servo motor for small robotics and RC applications.' }
    ],
    '🔌 Relay Modules & Power Control': [
      { id: 'relay-4ch', name: '5V 4-Channel Relay Module', price: 350, description: '5V 4-channel relay module for controlling multiple high-power devices.' },
      { id: 'relay-10a', name: '5V 10A Single-Channel Relay', price: 150, description: '5V 10A single-channel relay for high-current switching applications.' },
      { id: 'relay-2ch-j', name: '5V 2-Channel Relay (Jumper)', price: 250, description: '5V 2-channel relay module with jumper connections for dual device control.' }
    ],
    '🔋 Power Supply & Voltage Regulators': [
      { id: 'regulator-78xx', name: '7805 / 7812 Voltage Regulators', price: 65, description: '7805/7812 voltage regulators for stable 5V/12V power supply in circuits.' },
      { id: 'lm2596', name: 'LM2596 DC-DC Step-down Module', price: 250, description: 'LM2596 DC-DC step-down module for efficient voltage conversion.' },
      { id: 'power-9v', name: '9V, 1A DC Power Supply', price: 300, description: '9V 1A DC power supply adapter for powering Arduino and electronic projects.' }
    ]
  };

  // Get cart quantity for product
  const getCartQuantity = (productId) => {
    if (!cartItems || !Array.isArray(cartItems)) return 0;
    const cartItem = cartItems.find(item => item.id === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  // Show message
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Add to cart
  const handleAddToCart = (product, categoryName) => {
    if (!addToCart) {
      showMessage('❌ Cart functionality not available', 'error');
      return;
    }
    
    const cartProduct = {
      ...product,
      category: categoryName,
      image: '/api/placeholder/150/150' // Placeholder image
    };
    
    addToCart(cartProduct);
    showMessage(`✅ ${product.name} added to cart!`, 'success');
  };

  // Update cart quantity
  const handleUpdateQuantity = (product, newQuantity, categoryName) => {
    if (!updateCartItemQuantity || !addToCart) {
      showMessage('❌ Cart functionality not available', 'error');
      return;
    }
    
    if (newQuantity === 0) {
      updateCartItemQuantity(product.id, 0);
      showMessage(`❌ ${product.name} removed from cart`, 'info');
    } else {
      const cartProduct = {
        ...product,
        category: categoryName,
        image: '/api/placeholder/150/150'
      };
      
      if (getCartQuantity(product.id) === 0) {
        addToCart(cartProduct);
      } else {
        updateCartItemQuantity(product.id, newQuantity);
      }
      showMessage(`🔄 ${product.name} quantity updated`, 'info');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Product Categories</h2>
        <p className="text-gray-600">Browse our complete electronics catalog and add to cart</p>
      </div>

      {/* Message */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
          message.type === 'success' ? 'bg-green-600 text-white' :
          message.type === 'error' ? 'bg-red-600 text-white' :
          'bg-blue-600 text-white'
        }`}>
          {message.text}
        </div>
      )}

      {/* Category Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(productCatalog).map(([categoryName, products]) => (
          <div key={categoryName} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            {/* Category Header */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b border-blue-200">
              <h3 className="text-lg font-semibold text-blue-900">{categoryName}</h3>
              <p className="text-sm text-blue-700">{products.length} products available</p>
            </div>

            {/* Products List */}
            <div className="p-4">
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {products.slice(0, selectedCategory === categoryName ? products.length : 4).map((product) => {
                  const cartQuantity = getCartQuantity(product.id);
                  
                  return (
                    <div key={product.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">{product.name}</h4>
                          <p className="text-blue-600 font-bold">K{product.price}</p>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{product.description}</p>
                        </div>
                        
                        <div className="ml-3 flex flex-col items-end space-y-2">
                          {cartQuantity === 0 ? (
                            <button
                              onClick={() => handleAddToCart(product, categoryName)}
                              className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                              title="Add to Cart"
                            >
                              <ShoppingCart size={16} />
                            </button>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleUpdateQuantity(product, cartQuantity - 1, categoryName)}
                                className="bg-red-100 text-red-600 p-1 rounded hover:bg-red-200 transition-colors"
                              >
                                <Minus size={14} />
                              </button>
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                                {cartQuantity}
                              </span>
                              <button
                                onClick={() => handleUpdateQuantity(product, cartQuantity + 1, categoryName)}
                                className="bg-green-100 text-green-600 p-1 rounded hover:bg-green-200 transition-colors"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Show More/Less Button */}
              {products.length > 4 && (
                <button
                  onClick={() => setSelectedCategory(selectedCategory === categoryName ? null : categoryName)}
                  className="w-full mt-4 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                >
                  {selectedCategory === categoryName ? (
                    <>Show Less</>
                  ) : (
                    <>
                      Show All {products.length} Products
                      <ArrowRight size={16} className="ml-2" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* View Shop Button */}
      <div className="text-center mt-8">
        <a
          href="/shop"
          className="inline-flex items-center bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
        >
          <ShoppingCart className="mr-2" size={20} />
          View Full Shop
        </a>
      </div>
    </div>
  );
};

export default ProductCategoriesGrid;
